/** Typed API client (spec §6, §8.4, §10.3, §10.7): openapi-fetch over the generated schema. */
import createFetchClient, { type Middleware } from 'openapi-fetch'
import createQueryClient from 'openapi-react-query'
import type { paths } from './schema'

export type FieldErrors = Record<string, string[]>

/** An RFC 7807 problem from the API (spec §7.2). */
export class ApiProblem extends Error {
  readonly status: number
  readonly code: string
  readonly detail: string
  readonly fieldErrors: FieldErrors

  constructor(status: number, code: string, detail: string, fieldErrors: FieldErrors = {}) {
    super(detail || code)
    this.name = 'ApiProblem'
    this.status = status
    this.code = code
    this.detail = detail
    this.fieldErrors = fieldErrors
  }

  static from(body: unknown, status: number): ApiProblem {
    const b = (body && typeof body === 'object' ? body : {}) as Record<string, unknown>
    const errors = b.errors && typeof b.errors === 'object' ? (b.errors as FieldErrors) : {}
    return new ApiProblem(
      typeof b.status === 'number' ? b.status : status,
      typeof b.code === 'string' ? b.code : status >= 500 ? 'server_error' : 'error',
      typeof b.detail === 'string' ? b.detail : '',
      errors,
    )
  }
}

export function isProblem(error: unknown, code?: string): error is ApiProblem {
  return error instanceof ApiProblem && (code === undefined || error.code === code)
}

/** Data of a successful call, or throw the ApiProblem. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) throw ApiProblem.from(result.error, result.response.status)
  return result.data as T
}

// --- Events the shell listens to -------------------------------------------------------------

type Listener<A extends unknown[]> = (...args: A) => void
function channel<A extends unknown[]>() {
  const listeners = new Set<Listener<A>>()
  return {
    on(listener: Listener<A>) {
      listeners.add(listener)
      return () => void listeners.delete(listener)
    },
    emit(...args: A) {
      listeners.forEach((listener) => listener(...args))
    },
  }
}
const unauthorized = channel<[]>()
const versionChange = channel<[string]>()

export const apiEvents = {
  /** A 401 outside /login*: the session has ended (SessionExpiredDialog). */
  onUnauthorized: unauthorized.on,
  /** The server runs a different release than this page was loaded with. */
  onVersionChange: versionChange.on,
}

let knownVersion: string | null = null
export function resetKnownVersion(): void {
  knownVersion = null
}

// --- Middleware -----------------------------------------------------------------------------------

const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS'])

export function readCookie(name: string): string | null {
  const match = document.cookie.split('; ').find((part) => part.startsWith(`${name}=`))
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null
}

/** The CSRF cookie, fetched once when a write comes before any response has set it (e.g. a quick first login). */
async function csrfToken(): Promise<string | null> {
  const existing = readCookie('csrftoken')
  if (existing) return existing
  await globalThis.fetch(`${window.location.origin}/api/v1/auth/csrf/`, { credentials: 'same-origin' }).catch(() => undefined)
  return readCookie('csrftoken')
}

const middleware: Middleware = {
  async onRequest({ request }) {
    if (!SAFE_METHODS.has(request.method)) {
      const token = await csrfToken()
      if (token) request.headers.set('X-CSRFToken', token)
    }
    return request
  },
  onResponse({ response }) {
    const version = response.headers.get('X-App-Version')
    if (version) {
      if (knownVersion === null) knownVersion = version
      else if (version !== knownVersion) {
        knownVersion = version
        versionChange.emit(version)
      }
    }
    if (response.status === 401 && !window.location.pathname.startsWith('/login')) unauthorized.emit()
    return response
  },
}

export const api = createFetchClient<paths>({
  baseUrl: typeof window === 'undefined' ? '' : window.location.origin,
  credentials: 'same-origin',
  // Looked up per call, so test mocks and service-worker shims that patch fetch later still apply.
  fetch: (request) => globalThis.fetch(request),
})
api.use(middleware)

/** TanStack Query hooks: $api.useQuery('get', '/api/v1/me/'), $api.useMutation('post', ...). */
export const $api = createQueryClient(api)
